import { BACKEND_ERROR_TYPES, RATE_LIMIT_TYPES } from '@/types/error';
import axios from 'axios';

import { checkIfNetworkError, isAbortedRequest } from '@/shared/utils/axios';

import { getAccessToken } from './session';

export type TErrorType = 'AbortError' | 'HTTP Error' | 'Network Error' | 'Request Error';
export type TErrorMessage = string;
export type TStatus = 'idle' | 'loading' | 'success' | 'error';

export interface IErrorDetails {
	errorType: TErrorType;
	errorMessage: TErrorMessage;
	errorStatusCode: number | null;
	errorCode: number | null;
	isMessageFromBackend: boolean;
	isCancelledRequest: boolean;
	isNetworkError: boolean;
}

let cachedToken: string | null = null;

/**
 * Parse an error into display/branching details. PURE — it must never report to
 * Sentry: it is called from ~17 component and hook sites on errors the axios
 * interceptors have already reported, so reporting here multiplies every event by
 * the number of call sites. Reporting belongs in the interceptors only.
 */
export function getApiErrorDetails(error: unknown): IErrorDetails {
	if (isAbortedRequest(error)) {
		return {
			errorType: 'AbortError',
			errorMessage: 'Request was cancelled',
			errorStatusCode: null,
			errorCode: null,
			isMessageFromBackend: false,
			isCancelledRequest: true,
			isNetworkError: false,
		};
	}

	let errorType: TErrorType = 'Request Error';
	let errorMessage: TErrorMessage = '';
	let errorStatusCode: number | null = null;
	let errorCode: number | null = null;
	let isMessageFromBackend: boolean = false;
	let isNetworkError: boolean = false;

	if (axios.isAxiosError(error)) {
		if (error.response) {
			// The request was made and the server responded with a status code
			// that falls out of the range of 2xx
			const errorData = error.response.data as {
				message?: string;
				error_type?: string;
				error_code?: number;
				limit_type?: string;
			};

			// Check if this is an expected rate limit when AI credits are exhausted
			// (FREEMIUM_LIMIT_REACHED for AI_CREDITS is expected behavior, not a real error)
			const isExpectedRateLimit =
				error.response.status === 429 &&
				errorData?.error_type === BACKEND_ERROR_TYPES.FREEMIUM_LIMIT_REACHED &&
				errorData?.limit_type === RATE_LIMIT_TYPES.AI_CREDITS;

			// Only log as error if it's not an expected rate limit
			// Expected rate limits (FREEMIUM_LIMIT_REACHED for AI_CREDITS) are normal behavior
			// don't need to be logged
			if (!isExpectedRateLimit) {
				console.error('HTTP Error:', error.response.status, error.response.data);
			}

			const errorObj = error.response.data as { message: string };
			errorType = 'HTTP Error';
			errorMessage =
				errorObj?.message ??
				(error?.response?.status === 401
					? "You don't have permission to access this feature"
					: 'There was some problem. Please try again');
			errorStatusCode = error.response.status;
			errorCode = typeof errorData?.error_code === 'number' ? errorData.error_code : null;

			if (errorObj.message) isMessageFromBackend = true;
		} else if (error.request && checkIfNetworkError(error)) {
			// The request was made but no response was received (network error)
			console.error('Network Error:', error.request);
			errorType = 'Network Error';
			errorMessage = 'There was some problem. Please try again';
			isNetworkError = true;
		} else {
			// Something happened in setting up the request that triggered an Error
			console.error('Request Error:', error.message);
			errorType = 'Request Error';
			errorMessage = 'There was some problem. Please try again';
		}
	} else {
		// Handle the case where the error is not an AxiosError
		console.error('Error:', error);
		errorType = 'Request Error';
		errorMessage = 'There was some problem. Please try again';
	}

	return {
		errorType,
		errorMessage,
		errorStatusCode,
		errorCode,
		isMessageFromBackend,
		isCancelledRequest: false,
		isNetworkError,
	};
}

/**
 * Returns the agent Bearer token, reading from an in-memory cache.
 * On the first call (cache empty) it fetches the value from the HttpOnly
 * cookie via a server action and stores it for all subsequent calls.
 */
export const getAppAccessToken = async (): Promise<string | null> => {
	if (cachedToken) return cachedToken;
	cachedToken = await getAccessToken();
	return cachedToken;
};
